import { useSelector } from 'react-redux'
import { RootState } from '@/storage/store.ts'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { useCreateAccessHandbookLink } from '@/queries/help_links/hooks.ts'
import { useForm } from 'react-hook-form'
import {
  HandbookLinkFormSchema,
  HandbookLinkFormValues,
} from '@/pages/setup/link_handlers/handbook_link/handbook_links.schema.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog.tsx'
import { Button } from '@/app/common/components/button/Button'
import { PlusIcon } from '@/common/icons/general.tsx'
import { Input } from '@/app/common/components/input/Input'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { useState } from 'react'

interface Props {
  onAddHandbookLinkSuccess: () => void
}

const AddLinkPopup = (props: Props) => {
  const { onAddHandbookLinkSuccess } = props
  const [open, setOpen] = useState(false)

  const { selectedProgramYear } = useSelector(
    (state: RootState) => state.programs
  )

  const { toast } = useToast()

  const createProviderHandbookLink = useCreateAccessHandbookLink({
    onSuccess: () => {
      toast({
        title: 'Wallet Handbook Link Created',
        description: 'The Wallet handbook link has been created successfully.',
        variant: 'default',
      })
      onAddHandbookLinkSuccess()
      setOpen(false)
      reset()
    },
    onError: () => {
      toast({
        title: 'Error',
        description: `A link has already been created.`,
        variant: 'destructive',
      })
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<HandbookLinkFormValues>({
    resolver: zodResolver(HandbookLinkFormSchema),
    mode: 'onBlur',
    defaultValues: {
      link: '',
    },
  })

  const onSubmit = (data: HandbookLinkFormValues) => {
    createProviderHandbookLink.mutate({
      program_id: selectedProgramYear?.id ?? 0,
      link: data.link,
      globalOnId: 1,
    })
  }

  const handleClose = () => {
    setOpen(false)
    reset()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button className="h-10 w-10 rounded-full">
          <PlusIcon />
        </Button>
      </DialogTrigger>

      <DialogContent>
        <h2 className="font-medium">Add New Handbook Link</h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label>Handbook Link</label>
            <Input
              type="text"
              {...register('link')}
              aria-invalid={errors.link ? 'true' : 'false'}
            />
            {errors.link && (
              <p className="text-sm text-red-500 mt-1">{errors.link.message}</p>
            )}
          </div>
          <div className="flex space-x-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <div>
              <SubmitButton
                buttonTxt="Add Handbook Link"
                status={createProviderHandbookLink.status}
              />
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default AddLinkPopup
